import { useState } from "react";
import {
  useFloating,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
  safePolygon,
} from "@floating-ui/react";
import type { CSSProperties, ReactNode } from "react";
import type { Theme } from "../theme/pixel";
import { keywordSummary } from "../data/keywords";
import { xActionSummary } from "../data/xActions";

// Wraps a bolded keyword and shows the canonical description on hover.
// `keyword` may include a trailing parameter ("Volatile 5"); we strip
// it before looking up the summary.
export function KeywordTooltip({
  keyword,
  theme,
  children,
}: {
  keyword: string;
  theme: Theme;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "top",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: { open: 120, close: 60 },
    handleClose: safePolygon(),
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const lookup = keyword.replace(/\s+\d+$/, "").toLowerCase();
  const summary = keywordSummary(lookup) ?? xActionSummary(lookup);

  if (!summary) {
    return <>{children}</>;
  }

  const tooltipStyle: CSSProperties = {
    ...floatingStyles,
    background: theme.bgDeep,
    color: theme.ink,
    border: `2px solid ${theme.panelEdgeStrong}`,
    boxShadow: `4px 4px 0 0 rgba(0,0,0,0.4)`,
    padding: "8px 10px",
    fontFamily: theme.fontBody,
    fontSize: 12,
    lineHeight: 1.4,
    maxWidth: 280,
    zIndex: 2000,
    imageRendering: "pixelated",
    pointerEvents: "auto",
    fontWeight: 400,
  };

  const headingStyle: CSSProperties = {
    fontFamily: theme.fontDisplay,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: theme.letterSpacing,
    color: theme.accent,
    textTransform: theme.uppercase ? "uppercase" : "none",
    marginBottom: 4,
  };

  return (
    <>
      <span
        ref={refs.setReference}
        tabIndex={0}
        {...getReferenceProps()}
        style={{
          fontWeight: 700,
          // No `cursor: help` — the OS-rendered question-mark sprite
          // floats above the tooltip and obscures the first line.
          textDecoration: "underline dotted",
          textDecorationThickness: 1,
          textUnderlineOffset: 2,
        }}
      >
        {children}
      </span>
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={tooltipStyle}
            {...getFloatingProps()}
          >
            <div style={headingStyle}>{lookup}</div>
            <div>{summary}</div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
